import type { IApiResponse, ILoginRequest, ILoginResponse, IUser } from '@skystone/shared';

const API_BASE = '/api';

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
};

class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers: customHeaders, ...rest } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...customHeaders as Record<string, string>,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...rest,
    headers,
    credentials: 'same-origin',
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    if (response.status === 401 && !globalThis.location.pathname.startsWith('/login')) {
      globalThis.dispatchEvent(new CustomEvent('auth:session-expired'));
      throw new ApiError(response.status, 'Session expired');
    }
    const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new ApiError(response.status, errorData.error || 'Request failed');
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint, { method: 'GET' }),
  post: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: 'POST', body }),
  put: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: 'PUT', body }),
  patch: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: 'PATCH', body }),
  delete: <T>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),
};

export type LoginRequest = ILoginRequest;
export type User = Pick<IUser, 'id' | 'email' | 'role'>;

export const authApi = {
  login: async (credentials: LoginRequest): Promise<ILoginResponse> => {
    const result = await api.post<IApiResponse<ILoginResponse>>('/auth/login', credentials);
    return result.data;
  },
  me: async (): Promise<User> => {
    const result = await api.get<IApiResponse<User>>('/auth/me');
    return result.data;
  },
  logout: async (): Promise<void> => {
    await api.post<IApiResponse<{ message: string }>>('/auth/logout');
  },
  exchangeSsoToken: async (token: string): Promise<ILoginResponse> => {
    const result = await api.post<IApiResponse<ILoginResponse>>('/auth/set-token', { token });
    return result.data;
  },
};
